import React, { useState, useEffect, useCallback } from 'react';
import crmAcaoService from '../services/crmAcao.service';
import Modal from './Modal';

const COR = {
  azul:     '#003399', azulBg:  '#eff6ff',
  borda:    '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:   '#fff',    cinzaBg: '#fafafa',
  vermelho: '#e53935', vermBg:  '#fff0f0',
  verde:    '#1D9E75', verdeBg: '#f0fff4',
};

const inputStyle = {
  width: '100%', height: '36px', padding: '0 10px',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  fontSize: '13px', boxSizing: 'border-box',
  backgroundColor: COR.branco, color: COR.texto, outline: 'none',
};
const textareaStyle = {
  ...inputStyle,
  height: '80px', padding: '8px 10px',
  resize: 'vertical', fontFamily: 'inherit',
};

function hojeISO() {
  return new Date().toISOString().slice(0, 10);
}

function formatarDataBR(iso) {
  if (!iso) return '—';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

function formatarHora(t) {
  if (!t) return '';
  // Aceita "14:30:00.0000000" ou "14:30:00" ou Date
  const s = String(t);
  const m = s.match(/(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '';
}

// ─── Padrão de layout (Cadastro de Cliente): Seção com ícone SVG + chevron ───
const SvgChevron = ({ size = 16, color = 'currentColor', aberto }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
       style={{ transform: aberto ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const svgBase = (color) => ({ fill: 'none', stroke: color, strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' });
const SvgNovaAcao = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase(color)}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="12" y1="18" x2="12" y2="12" /><line x1="9" y1="15" x2="15" y2="15" />
  </svg>
);
const SvgHistorico = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase(color)}>
    <circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" />
  </svg>
);
function Secao({ icone: Icone, titulo, aberta, onToggle, children }) {
  return (
    <div style={{ border: `1px solid ${COR.borda}`, borderRadius: '6px', marginBottom: '12px', overflow: 'visible', backgroundColor: COR.branco }}>
      <div onClick={onToggle} style={{
        padding: '12px 16px', backgroundColor: aberta ? COR.azulBg : COR.cinzaBg,
        borderBottom: aberta ? `1px solid ${COR.borda}` : 'none',
        cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: '10px',
        transition: 'background-color 0.15s',
      }}>
        <span style={{ display: 'inline-flex', color: aberta ? COR.azul : COR.muted }}><Icone size={18} /></span>
        <span style={{ flex: 1, fontSize: '14px', fontWeight: '700', color: aberta ? COR.azul : COR.texto }}>{titulo}</span>
        <span style={{ display: 'inline-flex', color: COR.muted }}><SvgChevron aberto={aberta} size={16} /></span>
      </div>
      {aberta && <div style={{ padding: '16px' }}>{children}</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
function AcoesModal({ aberto, negocio, usuario, onFechar, onSucesso }) {
  const [acoes, setAcoes]           = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando]     = useState(false);
  const [erro, setErro]             = useState('');
  const [sucesso, setSucesso]       = useState('');

  // Form de inclusão
  const [dataAcao, setDataAcao] = useState(hojeISO());
  const [detalhes, setDetalhes] = useState('');

  // Seções colapsáveis (padrão)
  const [secNova, setSecNova] = useState(true);
  const [secHist, setSecHist] = useState(true);

  // Carregar ações ao abrir
  const carregar = useCallback(async () => {
    if (!negocio?.id) return;
    try {
      setCarregando(true);
      setErro('');
      const lista = await crmAcaoService.listarPorNegocio(negocio.id);
      setAcoes(lista);
    } catch (err) {
      console.error('Erro ao carregar ações:', err);
      setErro('Erro ao carregar ações.');
    } finally {
      setCarregando(false);
    }
  }, [negocio?.id]);

  useEffect(() => {
    if (aberto) {
      carregar();
      // Reset do form ao abrir
      setDataAcao(hojeISO());
      setDetalhes('');
      setErro(''); setSucesso('');
    }
  }, [aberto, carregar]);

  // Salvar nova ação
  const salvar = async () => {
    setErro(''); setSucesso('');
    if (!dataAcao) return setErro('Informe a data da ação.');
    if (!detalhes.trim()) return setErro('Detalhes da ação são obrigatórios.');

    try {
      setSalvando(true);
      await crmAcaoService.criar(negocio.id, {
        data_acao: dataAcao,
        detalhes:  detalhes.trim(),
        usuario_id: usuario?.id || null,
      });
      setSucesso('Ação registrada com sucesso!');
      // Limpa form
      setDataAcao(hojeISO());
      setDetalhes('');
      // Recarrega lista
      await carregar();
      // Notifica componente pai pra atualizar negocio.dt_ultima_acao
      if (onSucesso) onSucesso();
      setTimeout(() => setSucesso(''), 2500);
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Erro ao registrar ação.');
    } finally {
      setSalvando(false);
    }
  };

  // Dados auto-preenchidos no form (read-only)
  const agora = new Date();
  const horaAtual = `${String(agora.getHours()).padStart(2, '0')}:${String(agora.getMinutes()).padStart(2, '0')}`;
  const usuarioNome = usuario?.nomeAmigavel || usuario?.nome || usuario?.id || '—';

  return (
    <Modal
      aberto={aberto}
      titulo={`Ações · ${negocio?.id || ''}${negocio?.descricao ? ' · ' + negocio.descricao : ''}`}
      onFechar={onFechar}
      onSalvar={salvar}
      salvando={salvando}
      rotina="crm"
      descricao="Registre e acompanhe as ações de acompanhamento do negócio."
      botoesNoTopo
      largura="820px"
    >
      <div style={{ fontFamily: "'Manrope', sans-serif" }}>
        {erro && (
          <div style={{ backgroundColor: COR.vermBg, border: '1px solid #ffcccc', borderRadius: '5px', color: COR.vermelho, fontSize: '13px', padding: '10px 12px', marginBottom: '12px' }}>{erro}</div>
        )}
        {sucesso && (
          <div style={{ backgroundColor: COR.verdeBg, border: '1px solid #a8e6c1', borderRadius: '5px', color: '#1d6a3a', fontSize: '13px', padding: '10px 12px', marginBottom: '12px' }}>{sucesso}</div>
        )}

        {/* ───────── Nova Ação ───────── */}
        <Secao icone={SvgNovaAcao} titulo="Nova Ação" aberta={secNova} onToggle={() => setSecNova(v => !v)}>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr', gap: '18px' }}>
            <Campo label="Código">
              <input value={negocio?.id || '—'} disabled
                style={{ ...inputStyle, backgroundColor: COR.cinzaBg, color: COR.muted }} />
            </Campo>
            <Campo label="Data Registro">
              <input value={`${formatarDataBR(hojeISO())} ${horaAtual}`} disabled
                style={{ ...inputStyle, backgroundColor: COR.cinzaBg, color: COR.muted }} />
            </Campo>
            <Campo label="Usuário">
              <input value={usuarioNome} disabled
                style={{ ...inputStyle, backgroundColor: COR.cinzaBg, color: COR.muted }} />
            </Campo>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '200px', gap: '18px', marginTop: '18px' }}>
            <Campo label="Data da Ação" obrigatorio>
              <input type="date" value={dataAcao} onChange={e => setDataAcao(e.target.value)}
                max={hojeISO()} style={inputStyle} />
            </Campo>
          </div>
          <div style={{ marginTop: '18px' }}>
            <Campo label="Detalhes" obrigatorio>
              <textarea value={detalhes} onChange={e => setDetalhes(e.target.value)} maxLength={2000}
                placeholder="Ex: Enviado proposta por email; Visita ao cliente para apresentar escopo; Ligação de follow-up..."
                style={{ ...textareaStyle, height: '130px' }} />
            </Campo>
          </div>
        </Secao>

        {/* ───────── Histórico ───────── */}
        <Secao icone={SvgHistorico} titulo={`Histórico (${acoes.length})`} aberta={secHist} onToggle={() => setSecHist(v => !v)}>
          {carregando ? (
            <div style={{ padding: '20px', textAlign: 'center', fontSize: '12px', color: COR.muted }}>Carregando...</div>
          ) : acoes.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', fontSize: '12px', color: COR.muted, backgroundColor: COR.cinzaBg, borderRadius: '5px', border: `1px dashed ${COR.borda}` }}>
              Nenhuma ação registrada ainda. Registre a primeira acima!
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {acoes.map(a => (
                <div key={a.id} style={{ border: `1px solid ${COR.borda}`, borderRadius: '6px', padding: '10px 14px', backgroundColor: COR.branco }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: COR.azul }}>{formatarDataBR(a.data_acao)}</span>
                    <span style={{ fontSize: '11px', color: COR.muted }}>por {a.usuario_nome || a.usuario_id || '—'}</span>
                    <span style={{ fontSize: '10px', color: COR.muted, marginLeft: 'auto' }}>Registrado em {formatarDataBR(a.data_registro)} às {formatarHora(a.hora_registro)}</span>
                  </div>
                  <div style={{ fontSize: '13px', color: COR.texto, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.4' }}>{a.detalhes}</div>
                </div>
              ))}
            </div>
          )}
        </Secao>
      </div>
    </Modal>
  );
}

function Campo({ label, children, obrigatorio }) {
  return (
    <div style={{ minWidth: 0 }}>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: COR.muted, marginBottom: '4px' }}>
        {label}{obrigatorio && <span style={{ color: COR.vermelho }}> *</span>}
      </label>
      {children}
    </div>
  );
}

export default AcoesModal;
